import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  ButtonBase,
  Checkbox,
  InputAdornment,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Popover,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';

import { palette } from '../../theme';
const BRAND = palette.primary.main;
const TINT = palette.surface.tint;
const MUTED = palette.text.secondary;
/** Above this many options the list gets its own search. */
const SEARCH_THRESHOLD = 6;

export interface FilterOption {
  value: string;
  label: string;
}

/** Pick any number of values; applied the moment one is ticked. */
export interface MultiFilter {
  kind: 'multi';
  key: string;
  label: string;
  options: FilterOption[];
  selected: string[];
  onChange: (selected: string[]) => void;
}

/** On or off, like "only debtors". `defaultActive` is how the list opens, and gets no chip. */
export interface ToggleFilter {
  kind: 'toggle';
  key: string;
  label: string;
  active: boolean;
  defaultActive?: boolean;
  onChange: (active: boolean) => void;
}

export type ListFilter = MultiFilter | ToggleFilter;

interface ListToolbarProps {
  search?: { value: string; onChange: (value: string) => void; placeholder: string };
  filters?: ListFilter[];
  /** Controls that are not filters, such as a date range, placed after the filters. */
  extra?: React.ReactNode;
  /** "12 of 40 children", right-aligned. */
  resultText?: string;
  /** Resets search and every filter. Shown beside the chips. */
  onClearAll?: () => void;
}

const filterButtonSx = (on: boolean) => ({
  height: 40,
  px: 1.5,
  gap: 0.5,
  borderRadius: '10px',
  fontSize: 14,
  border: `1px solid ${on ? BRAND : palette.field.border}`,
  backgroundColor: on ? TINT : palette.surface.main,
  color: on ? BRAND : 'text.primary',
  whiteSpace: 'nowrap'
});

/**
 * Debounced, but follows the URL: "Clear all" or a back navigation that changes the
 * value must show up in the field, not just in the results.
 */
const SearchBox: React.FC<NonNullable<ListToolbarProps['search']>> = ({ value, onChange, placeholder }) => {
  const [text, setText] = useState(value);
  const latest = useRef(value);

  useEffect(() => {
    if (value !== latest.current) {
      latest.current = value;
      setText(value);
    }
  }, [value]);

  useEffect(() => {
    if (text === latest.current) return undefined;
    const timer = setTimeout(() => {
      latest.current = text;
      onChange(text);
    }, 400);
    return () => clearTimeout(timer);
    // onChange is a fresh closure on every render of the list
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text]);

  return (
    <TextField
      value={text}
      onChange={event => setText(event.target.value)}
      placeholder={placeholder}
      size="small"
      slotProps={{
        htmlInput: { 'aria-label': placeholder },
        input: {
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ color: MUTED }} />
            </InputAdornment>
          )
        }
      }}
      sx={{ width: 320, maxWidth: '100%', '& .MuiOutlinedInput-root': { height: 40, borderRadius: '10px' } }}
    />
  );
};

const MultiFilterButton: React.FC<{ filter: MultiFilter }> = ({ filter }) => {
  const { t } = useTranslation();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [query, setQuery] = useState('');
  const count = filter.selected.length;
  const shown = filter.options.filter(option => option.label.toLowerCase().includes(query.trim().toLowerCase()));

  const toggle = (value: string) =>
    filter.onChange(filter.selected.includes(value) ? filter.selected.filter(item => item !== value) : [...filter.selected, value]);

  return (
    <>
      <ButtonBase
        aria-haspopup="dialog"
        aria-expanded={!!anchor}
        onClick={event => {
          setQuery('');
          setAnchor(event.currentTarget);
        }}
        sx={filterButtonSx(count > 0)}
      >
        {count ? t('filters.withCount', { label: filter.label, count }) : filter.label}
        <ExpandMoreIcon sx={{ fontSize: 18 }} />
      </ButtonBase>
      <Popover
        open={!!anchor}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{ paper: { elevation: 3, sx: { borderRadius: '10px', mt: 0.5, minWidth: 260, maxWidth: 360 } } }}
      >
        {filter.options.length > SEARCH_THRESHOLD && (
          <Box sx={{ p: 1.5, pb: 0.5 }}>
            <TextField
              autoFocus
              fullWidth
              size="small"
              value={query}
              placeholder={t('filters.search')}
              onChange={event => setQuery(event.target.value)}
              slotProps={{ htmlInput: { 'aria-label': t('filters.search') } }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
            />
          </Box>
        )}
        <List dense role="group" aria-label={filter.label} sx={{ maxHeight: 320, overflowY: 'auto' }}>
          {shown.map(option => {
            const checked = filter.selected.includes(option.value);
            return (
              <ListItemButton key={option.value} onClick={() => toggle(option.value)} role="checkbox" aria-checked={checked}>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <Checkbox edge="start" size="small" checked={checked} tabIndex={-1} disableRipple slotProps={{ input: { 'aria-hidden': true } }} />
                </ListItemIcon>
                <ListItemText primary={option.label} slotProps={{ primary: { sx: { fontSize: 14 } } }} />
              </ListItemButton>
            );
          })}
          {!shown.length && <Typography sx={{ fontSize: 14, color: MUTED, px: 2, py: 1 }}>{t('filters.noOptions')}</Typography>}
        </List>
      </Popover>
    </>
  );
};

const ToggleFilterButton: React.FC<{ filter: ToggleFilter }> = ({ filter }) => (
  <ButtonBase aria-pressed={filter.active} onClick={() => filter.onChange(!filter.active)} sx={filterButtonSx(filter.active)}>
    {filter.label}
  </ButtonBase>
);

const FilterChip: React.FC<{ label: string; removeLabel: string; onRemove: () => void }> = ({ label, removeLabel, onRemove }) => (
  <Box
    component="span"
    sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25, height: 28, pl: 1.25, pr: 0.25, borderRadius: 999, backgroundColor: TINT, fontSize: 13 }}
  >
    {label}
    <ButtonBase aria-label={removeLabel} onClick={onRemove} sx={{ width: 24, height: 24, borderRadius: '50%' }}>
      <CloseIcon sx={{ fontSize: 16 }} />
    </ButtonBase>
  </Box>
);

/**
 * The bar above a list: search, filters as buttons, how many results, and a row of the
 * filters in force that can be removed one by one.
 *
 * Filters apply as they are clicked. The old pickers asked for a "Show results" press,
 * which meant a ticked box that had visibly not done anything yet.
 */
const ListToolbar: React.FC<ListToolbarProps> = ({ search, filters = [], extra, resultText, onClearAll }) => {
  const { t } = useTranslation();

  const chips = filters.flatMap(filter => {
    if (filter.kind === 'toggle') {
      if (filter.active === (filter.defaultActive ?? false)) return [];
      const label = filter.active ? filter.label : t('filters.notToggle', { label: filter.label });
      return [{ key: filter.key, label, onRemove: () => filter.onChange(filter.defaultActive ?? false) }];
    }
    return filter.selected.map(value => {
      const option = filter.options.find(item => item.value === value);
      const label = t('filters.chip', { label: filter.label, value: option?.label ?? value });
      return { key: `${filter.key}:${value}`, label, onRemove: () => filter.onChange(filter.selected.filter(item => item !== value)) };
    });
  });

  return (
    <Box sx={{ px: 2.5, py: 2 }}>
      <Stack direction="row" alignItems="center" spacing={1} useFlexGap flexWrap="wrap">
        {search && <SearchBox {...search} />}
        {filters.map(filter =>
          filter.kind === 'toggle' ? <ToggleFilterButton key={filter.key} filter={filter} /> : <MultiFilterButton key={filter.key} filter={filter} />
        )}
        {extra}
        {resultText && (
          <Typography sx={{ fontSize: 14, color: MUTED, ml: 'auto !important', pl: 1 }} aria-live="polite">
            {resultText}
          </Typography>
        )}
      </Stack>
      {chips.length > 0 && (
        <Stack direction="row" alignItems="center" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 1.5 }}>
          {chips.map(chip => (
            <FilterChip key={chip.key} label={chip.label} removeLabel={t('filters.remove', { label: chip.label })} onRemove={chip.onRemove} />
          ))}
          {onClearAll && (
            <ButtonBase onClick={onClearAll} sx={{ color: BRAND, fontSize: 13, px: 1, py: 0.5, borderRadius: 2, '&:hover': { backgroundColor: TINT } }}>
              {t('filters.clearAll')}
            </ButtonBase>
          )}
        </Stack>
      )}
    </Box>
  );
};

export default ListToolbar;
